import { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';

const AdminAuthContext = createContext(null);

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;

async function checkAdminRole(userId, accessToken) {
  try {
    const res = await fetch(
      `${SUPABASE_URL}/rest/v1/profiles?select=role&id=eq.${userId}&limit=1`,
      {
        headers: {
          'apikey': SUPABASE_KEY,
          'Authorization': `Bearer ${accessToken}`,
        },
        signal: AbortSignal.timeout(8000),
      }
    );
    const profiles = await res.json();
    return Array.isArray(profiles) && profiles[0]?.role === 'admin';
  } catch {
    return false;
  }
}

export function AdminAuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    supabase.auth.getSession().then(async ({ data: { session: s } }) => {
      if (!mounted) return;
      if (s) {
        const admin = await checkAdminRole(s.user.id, s.access_token);
        if (mounted) { setSession(s); setIsAdmin(admin); }
      }
      if (mounted) setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (_event, s) => {
      if (!mounted) return;
      if (s) {
        const admin = await checkAdminRole(s.user.id, s.access_token);
        if (mounted) { setSession(s); setIsAdmin(admin); }
      } else {
        if (mounted) { setSession(null); setIsAdmin(false); }
      }
      if (mounted) setLoading(false);
    });

    return () => { mounted = false; subscription.unsubscribe(); };
  }, []);

  async function signIn(email, password) {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw new Error('INVALID_CREDENTIALS');
    const admin = await checkAdminRole(data.user.id, data.session?.access_token);
    if (!admin) {
      supabase.auth.signOut();
      throw new Error('NOT_ADMIN');
    }
    return data;
  }

  async function signOut() {
    await supabase.auth.signOut();
    setSession(null);
    setIsAdmin(false);
  }

  return (
    <AdminAuthContext.Provider value={{ session, isAdmin, loading, signIn, signOut }}>
      {children}
    </AdminAuthContext.Provider>
  );
}

export function useAdminAuthContext() {
  return useContext(AdminAuthContext);
}
